import { MessageSquareTextIcon } from "lucide-react";
import { useCallback } from "react";
import { toast } from "sonner";
import {
	DropdownMenuItem,
	DropdownMenuPortal,
	DropdownMenuSub,
	DropdownMenuSubContent,
	DropdownMenuSubTrigger,
} from "@/components/ui/dropdown-menu";
import { copyToClipboard } from "@/lib/utils";

export interface ChatCopyTemplate {
	/** Template key the upstream registry knows — see internal/manager/order.templates. */
	key: string;
	/** Name the team uses for the copy, kept as-is in both locales. */
	label: string;
}

/** Order copies: GET /api/v1/appointments/orders/:id/chat-copy?template= */
export const ORDER_CHAT_COPY_TEMPLATES: ChatCopyTemplate[] = [
	{ key: "qontak-invoice-billing", label: "Penagihan Invoice" },
	{ key: "qontak-schedule-confirmation", label: "Info Jadwal Pasien ACC" },
	{ key: "karpis-schedule-info", label: "Karpis — Info Jadwal Pasien" },
	{
		key: "karpis-schedule-acc-pending-invoice",
		label: "Karpis — Pasien ACC, Invoice Menyusul",
	},
	{
		key: "karpis-schedule-acc-paid",
		label: "Karpis — Pasien ACC, Sudah Ada Invoice",
	},
	{
		key: "mitra-schedule-acc-pending-payment",
		label: "Mitra — Info Pasien ACC (Belum Bayar)",
	},
	{ key: "mitra-attendance-v1", label: "Mitra — Absensi V1" },
	{ key: "mitra-attendance-v2", label: "Mitra — Absensi V2" },
	{
		key: "mitra-attendance-v2-perawat-homecare",
		label: "Mitra — Absensi V2 (Perawat Homecare)",
	},
	{ key: "rocket-chat-schedule-acc", label: "Rocket Chat — Pasien ACC Jadwal" },
];

/** Draft copies: a separate upstream registry, sourced from form_data. */
export const DRAFT_CHAT_COPY_TEMPLATES: ChatCopyTemplate[] = [
	{ key: "mitra-lock-schedule", label: "Mitra — Lock Jadwal" },
	{
		key: "qontak-schedule-info-single-visit",
		label: "Info Jadwal Visit Langsung",
	},
	{ key: "qontak-schedule-info-package", label: "Info Jadwal Visit Paket" },
	{
		key: "rocket-chat-schedule-awaiting-confirmation",
		label: "Rocket Chat — Menunggu Konfirmasi Pasien",
	},
	{ key: "rocket-chat-schedule-acc", label: "Rocket Chat — Pasien ACC Jadwal" },
];

/**
 * Submenu that fetches one ready-to-paste chat copy and puts it on the clipboard.
 *
 * Self-contained on purpose: both tables already thread a long list of handlers
 * through their column factories, and this needs none of them — only the row's
 * own endpoint.
 */
export const ChatCopyMenu = ({
	endpoint,
	templates,
	label,
	disabled,
}: {
	/** Row endpoint, e.g. `/api/v1/appointments/orders/<id>/chat-copy`. */
	endpoint: string;
	templates: ChatCopyTemplate[];
	label: string;
	disabled?: boolean;
}) => {
	const onPick = useCallback(
		async ({ key, label: name }: ChatCopyTemplate) => {
			const loading = toast.loading(`Menyiapkan "${name}"...`);
			try {
				const response = await fetch(
					`${endpoint}?template=${encodeURIComponent(key)}`,
					{ headers: { Accept: "application/json" } },
				);
				const body = await response.json();
				const text: string | undefined = body?.data?.text;
				if (!response.ok || !text) {
					throw new Error(body?.error || body?.message || "unknown error");
				}

				if (!(await copyToClipboard(text))) {
					toast.error(`Gagal menyalin "${name}"`, { id: loading });
					return;
				}

				// Upstream fills unknown placeholders with "-" and reports them; the copy is
				// still usable, but the admin has to know what to complete before sending.
				const missing: string[] = body.data.missing ?? [];
				if (missing.length) {
					toast.warning(
						`"${name}" disalin — data kosong: ${missing.join(", ")}`,
						{ id: loading },
					);
				} else {
					toast.success(`"${name}" disalin`, { id: loading });
				}
			} catch (error) {
				console.error(`Failed to build chat copy "${key}": ${error}`);
				toast.error(`Gagal menyiapkan "${name}"`, { id: loading });
			}
		},
		[endpoint],
	);

	return (
		<DropdownMenuSub>
			<DropdownMenuSubTrigger disabled={disabled}>
				<MessageSquareTextIcon className="opacity-60" aria-hidden="true" />
				{label}
			</DropdownMenuSubTrigger>

			<DropdownMenuPortal>
				<DropdownMenuSubContent>
					{templates.map((template) => (
						<DropdownMenuItem
							key={template.key}
							onSelect={() => {
								onPick(template);
							}}
						>
							{template.label}
						</DropdownMenuItem>
					))}
				</DropdownMenuSubContent>
			</DropdownMenuPortal>
		</DropdownMenuSub>
	);
};
